import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEventHandler } from 'react';
import { filesFromDrop } from 'src/lib/file-drop';

/**
 * True when a drag carries OS files. Gates the drop target so text/selection
 * drags (and webmail attachment chips, which are only URL/HTML strings) never
 * trigger the overlay or get their default drop behavior suppressed.
 */
export function dragCarriesFiles(types: ReadonlyArray<string>): boolean {
  return types.includes('Files');
}

/** Spread onto the container element that should accept dropped files. */
export interface FileDropTargetProps {
  onDragEnter: DragEventHandler<HTMLElement>;
  onDragOver: DragEventHandler<HTMLElement>;
  onDragLeave: DragEventHandler<HTMLElement>;
  onDrop: DragEventHandler<HTMLElement>;
}

interface UseFileDropOptions {
  /**
   * Receives the dropped files. Extraction happens synchronously inside the
   * drop handler via filesFromDrop — Outlook virtual files are gone afterwards.
   */
  onDropFiles: (files: File[]) => void;
  /**
   * Suppresses the drag-active state and ignores drops (e.g. while an upload
   * is already in flight) without unbinding the handlers.
   */
  disabled?: boolean;
}

/**
 * Turns a container into an always-on file drop target.
 *
 * - dragenter/dragleave fire once per child element crossed, so a depth
 *   counter tracks when the pointer truly leaves the container.
 * - While mounted, window-level dragover/drop listeners preventDefault on
 *   file drags so a drop that misses the container doesn't navigate the tab
 *   away (losing both page state and the file). They only touch file drags —
 *   dragging text into an input keeps its default behavior.
 */
export function useFileDrop({
  onDropFiles,
  disabled = false,
}: UseFileDropOptions): {
  isDragActive: boolean;
  dropTargetProps: FileDropTargetProps;
} {
  const [isDragActive, setIsDragActive] = useState(false);
  const depthRef = useRef(0);

  const deactivate = useCallback(() => {
    depthRef.current = 0;
    setIsDragActive(false);
  }, []);

  const onDragEnter = useCallback<DragEventHandler<HTMLElement>>(
    (e) => {
      if (disabled || !dragCarriesFiles(e.dataTransfer.types)) return;
      depthRef.current += 1;
      setIsDragActive(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback<DragEventHandler<HTMLElement>>(
    (e) => {
      if (disabled || !dragCarriesFiles(e.dataTransfer.types)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragActive(false);
    },
    [disabled],
  );

  const onDragOver = useCallback<DragEventHandler<HTMLElement>>(
    (e) => {
      if (disabled || !dragCarriesFiles(e.dataTransfer.types)) return;
      // Without this the browser refuses the drop and fires no drop event.
      e.preventDefault();
    },
    [disabled],
  );

  const onDrop = useCallback<DragEventHandler<HTMLElement>>(
    (e) => {
      e.preventDefault();
      deactivate();
      if (disabled || !dragCarriesFiles(e.dataTransfer.types)) return;
      // Synchronous by contract — see filesFromDrop.
      const files = filesFromDrop(e.dataTransfer);
      if (files.length > 0) onDropFiles(files);
    },
    [disabled, onDropFiles, deactivate],
  );

  useEffect(() => {
    function handleWindowDragOver(event: DragEvent) {
      if (event.dataTransfer && dragCarriesFiles(event.dataTransfer.types)) {
        event.preventDefault();
      }
    }
    function handleWindowDrop(event: DragEvent) {
      if (event.dataTransfer && dragCarriesFiles(event.dataTransfer.types)) {
        event.preventDefault();
      }
      // A drop anywhere ends the drag — clear a stuck overlay even when the
      // depth counter desynced (e.g. mid-drag DOM churn).
      deactivate();
    }
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);
    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [deactivate]);

  return {
    isDragActive,
    dropTargetProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
